/**
 * Scenario Projection Line Charts for Valuation Scorecard.
 *
 * Renders Highcharts line charts showing High/Base/Low scenario projections
 * for key financial metrics (Revenue, Net Profit, EPS, etc.) over time.
 * Includes arearange confidence band between High and Low scenarios.
 */

import type Highcharts from "highcharts";
import { isDarkMode, setupThemeObserver } from "../../common/chartTheme";
import { createChartLifecycle } from "./chartLifecycle";

/** A single data point in a scenario series. */
interface ScenarioPoint {
  x: string;
  y: number;
}

/** A range data point pairing High and Low values at an x-axis position. */
interface RangePoint {
  x: string;
  low: number;
  high: number;
}

/** Chart configuration from the Django template. */
export interface ScenarioChartConfig {
  title: string;
  unit: string;
  format: string;
  series: Record<string, ScenarioPoint[]>;
  range?: RangePoint[];
}

/** Scenario color configuration. */
interface ScenarioColors {
  high: string;
  base: string;
  low: string;
}

/** Theme colors for the chart. */
interface ChartThemeColors {
  textColor: string;
  subtitleColor: string;
  gridLineColor: string;
  backgroundColor: string;
  scenarios: ScenarioColors;
  historicalDot: string;
}

let themeObserver: MutationObserver | null = null;

function getThemeColors(): ChartThemeColors {
  const dark = isDarkMode();
  return {
    textColor: dark ? "#E0E0E3" : "#333333",
    subtitleColor: dark ? "#A0A0A3" : "#666666",
    gridLineColor: dark ? "#374151" : "#e5e7eb",
    backgroundColor: "transparent",
    scenarios: {
      high: dark ? "#4ade80" : "#22c55e",
      base: dark ? "#fbbf24" : "#f59e0b",
      low: dark ? "#f87171" : "#ef4444",
    },
    historicalDot: dark ? "#60a5fa" : "#3b82f6",
  };
}

function formatValue(value: number, format: string): string {
  switch (format) {
    case "millions":
      if (Math.abs(value) >= 1_000) {
        return `${(value / 1_000).toFixed(1)}b`;
      }
      return `${value.toFixed(0)}m`;
    case "percent":
      return `${value.toFixed(1)}%`;
    case "decimal":
      return value.toFixed(2);
    default:
      return value.toFixed(2);
  }
}

function getTooltipUnit(format: string, unit: string): string {
  if (!unit) return "";
  if (format === "percent") {
    return "";
  }
  if (format === "millions") {
    if (unit === "m") return "";
    if (unit.endsWith(" m")) {
      return unit.slice(0, -2);
    }
  }
  return unit;
}

function formatTooltipValue(value: number, format: string, unit: string): string {
  const formatted = formatValue(value, format);
  const tooltipUnit = getTooltipUnit(format, unit);
  return tooltipUnit ? `${formatted} ${tooltipUnit}` : formatted;
}

function renderChartForContainer(container: HTMLElement): void {
  const dataAttr = container.dataset.chartConfig;
  if (!dataAttr) {
    lifecycle.destroyChart(container.id);
    return;
  }

  try {
    const config = JSON.parse(dataAttr) as ScenarioChartConfig;
    if (!config?.series || Object.keys(config.series).length === 0) {
      lifecycle.destroyChart(container.id);
      return;
    }

    if (!lifecycle.isContainerVisible(container)) {
      lifecycle.destroyChart(container.id);
      return;
    }

    lifecycle.destroyChart(container.id);
    const chart = renderScenarioChart(container.id, config);
    if (chart) {
      lifecycle.instances.set(container.id, chart);
    }
  } catch (e) {
    console.warn(`Failed to parse scenario chart data for ${container.id}:`, e);
  }
}

const lifecycle = createChartLifecycle(".scenario-projection-chart", renderChartForContainer);

/**
 * Renders a scenario projection line chart.
 *
 * @param containerId - The ID of the container element.
 * @param config - The chart configuration from Django template.
 * @returns The Highcharts chart instance, or null if rendering failed.
 */
export function renderScenarioChart(
  containerId: string,
  config: ScenarioChartConfig,
): Highcharts.Chart | null {
  const container = document.getElementById(containerId);
  if (!container || !window.Highcharts) {
    return null;
  }

  const colors = getThemeColors();

  // Build categories (x-axis labels) from the longest series
  const allXValues = new Set<string>();
  for (const points of Object.values(config.series)) {
    for (const pt of points) {
      allXValues.add(pt.x);
    }
  }
  if (config.range) {
    for (const rp of config.range) {
      allXValues.add(rp.x);
    }
  }
  const categories = Array.from(allXValues).sort();

  if (categories.length === 0) {
    return null;
  }

  // Determine which point is historical (first point if it's the base year)
  const scenarioOrder: Array<{
    key: string;
    name: string;
    color: string;
    dashStyle: string;
    lineWidth: number;
    zIndex: number;
    markerRadius: number;
  }> = [
    {
      key: "high",
      name: "High",
      color: colors.scenarios.high,
      dashStyle: "ShortDash",
      lineWidth: 1.5,
      zIndex: 2,
      markerRadius: 2.5,
    },
    {
      key: "base",
      name: "Base",
      color: colors.scenarios.base,
      dashStyle: "Solid",
      lineWidth: 2.5,
      zIndex: 3,
      markerRadius: 4,
    },
    {
      key: "low",
      name: "Low",
      color: colors.scenarios.low,
      dashStyle: "ShortDash",
      lineWidth: 1.5,
      zIndex: 2,
      markerRadius: 2.5,
    },
  ];

  const chartSeries: Highcharts.SeriesOptionsType[] = [];

  // Add arearange confidence band between High and Low (rendered first, behind lines)
  if (config.range && config.range.length > 0) {
    const rangeData: Array<[number, number, number]> = [];
    for (const rp of config.range) {
      const catIdx = categories.indexOf(rp.x);
      if (catIdx >= 0) {
        rangeData.push([catIdx, rp.low, rp.high]);
      }
    }

    if (rangeData.length > 0) {
      const dark = isDarkMode();
      chartSeries.push({
        type: "arearange",
        name: "Range",
        data: rangeData,
        color: dark ? "rgba(74, 222, 128, 0.10)" : "rgba(34, 197, 94, 0.08)",
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, dark ? "rgba(74, 222, 128, 0.15)" : "rgba(34, 197, 94, 0.12)"],
            [1, dark ? "rgba(248, 113, 113, 0.15)" : "rgba(239, 68, 68, 0.12)"],
          ],
        } as unknown as string,
        lineWidth: 0,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
        zIndex: 0,
      } as Highcharts.SeriesOptionsType);
    }
  }

  // Add scenario lines
  for (const scenario of scenarioOrder) {
    const points = config.series[scenario.key];
    if (!points || points.length === 0) continue;

    const data: Array<number | null> = categories.map((cat) => {
      const pt = points.find((p) => p.x === cat);
      return pt ? pt.y : null;
    });

    const isBase = scenario.key === "base";

    chartSeries.push({
      type: "line",
      name: scenario.name,
      data,
      color: scenario.color,
      dashStyle: scenario.dashStyle as Highcharts.DashStyleValue,
      lineWidth: scenario.lineWidth,
      zIndex: scenario.zIndex,
      marker: {
        enabled: true,
        radius: scenario.markerRadius,
        symbol: "circle",
        fillColor: isBase ? "#ffffff" : scenario.color,
        lineColor: scenario.color,
        lineWidth: isBase ? 2 : 0,
      },
      connectNulls: true,
    });
  }

  if (chartSeries.length === 0) {
    return null;
  }

  // Mark the first category as historical if it differs from projection years
  const hasHistorical = chartSeries.some((s) => {
    if (s.type === "arearange") return false;
    const typedSeries = s as Highcharts.SeriesLineOptions;
    return typedSeries.data && (typedSeries.data as Array<number | null>)[0] !== null;
  });

  const chart = window.Highcharts.chart(containerId, {
    chart: {
      type: "line",
      backgroundColor: colors.backgroundColor,
      height: 250,
      spacing: [10, 15, 10, 10],
    },
    title: {
      text: config.title,
      style: {
        fontSize: "13px",
        fontWeight: "600",
        color: colors.textColor,
      },
      align: "left",
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
    legend: {
      enabled: true,
      align: "right",
      verticalAlign: "top",
      layout: "horizontal",
      floating: true,
      y: -5,
      itemStyle: {
        color: colors.subtitleColor,
        fontSize: "10px",
        fontWeight: "normal",
      },
    },
    xAxis: {
      categories,
      labels: {
        style: {
          color: colors.subtitleColor,
          fontSize: "10px",
        },
      },
      lineColor: colors.gridLineColor,
      tickLength: 0,
      plotBands:
        hasHistorical && categories.length > 1
          ? [
              {
                from: -0.5,
                to: 0.5,
                color: isDarkMode() ? "rgba(96, 165, 250, 0.08)" : "rgba(59, 130, 246, 0.06)",
                label: {
                  text: "Actual",
                  style: {
                    color: isDarkMode() ? "rgba(96, 165, 250, 0.5)" : "rgba(59, 130, 246, 0.4)",
                    fontSize: "8px",
                  },
                  align: "center",
                  verticalAlign: "bottom",
                  y: -2,
                },
              },
            ]
          : [],
    },
    yAxis: {
      title: {
        text: config.unit,
        style: {
          color: colors.subtitleColor,
          fontSize: "10px",
        },
      },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
          const val = this.value as number;
          return formatValue(val, config.format);
        },
        style: {
          color: colors.subtitleColor,
          fontSize: "9px",
        },
      },
      gridLineColor: colors.gridLineColor,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function (this: Highcharts.TooltipFormatterContextObject): string {
        let html = `<div style="font-size:12px"><strong>${this.x}</strong></div>`;
        let highVal: number | null = null;
        let lowVal: number | null = null;

        if (this.points) {
          for (const pt of this.points) {
            // Skip arearange series in tooltip
            if (pt.series.type === "arearange") continue;

            const val =
              pt.y !== undefined && pt.y !== null
                ? formatTooltipValue(pt.y, config.format, config.unit)
                : "\u2013";
            html += `<div><span style="color:${pt.color}">\u25CF</span> ${pt.series.name}: <strong>${val}</strong></div>`;

            if (pt.series.name === "High" && pt.y != null) highVal = pt.y;
            if (pt.series.name === "Low" && pt.y != null) lowVal = pt.y;
          }

          // Show spread between High and Low
          if (highVal !== null && lowVal !== null) {
            const baseVal = this.points.find((p) => p.series.name === "Base")?.y;
            if (baseVal != null && baseVal !== 0) {
              const upside = (((highVal - baseVal) / Math.abs(baseVal)) * 100).toFixed(1);
              const downside = (((lowVal - baseVal) / Math.abs(baseVal)) * 100).toFixed(1);
              html += `<div style="margin-top:3px;font-size:10px;color:${colors.subtitleColor}">Spread: <span style="color:${colors.scenarios.high}">+${upside}%</span> / <span style="color:${colors.scenarios.low}">${downside}%</span></div>`;
            }
          }
        }
        return html;
      },
    },
    plotOptions: {
      line: {
        animation: { duration: 600, easing: "easeOutQuart" },
      },
      arearange: {
        animation: { duration: 600 },
      },
    },
    series: chartSeries,
  } as Highcharts.Options) as Highcharts.Chart;

  return chart;
}

/**
 * Initializes all scenario projection charts on the page.
 *
 * Finds all elements with the .scenario-projection-chart class and renders
 * line charts based on their data-chart-config attributes.
 */
export function initializeScenarioCharts(): void {
  const chartContainers = document.querySelectorAll<HTMLElement>(".scenario-projection-chart");

  const activeIds = new Set<string>();

  chartContainers.forEach((container) => {
    if (!container.id) return;
    activeIds.add(container.id);
    renderChartForContainer(container);
  });

  // Clean up stale chart instances
  for (const containerId of lifecycle.instances.keys()) {
    if (!activeIds.has(containerId) || !document.getElementById(containerId)) {
      lifecycle.destroyChart(containerId);
    }
  }

  // Hidden containers can render later through reflow without running this initializer.
  if (activeIds.size === 0) {
    if (themeObserver) {
      themeObserver.disconnect();
      themeObserver = null;
    }
    return;
  }

  if (!themeObserver) {
    themeObserver = setupThemeObserver(() => {
      initializeScenarioCharts();
    });
  }
}

/**
 * Registers DOM listeners to auto-initialize scenario projection charts.
 */
export function setupScenarioChartAutoInit(): void {
  lifecycle.setupAutoInit(initializeScenarioCharts);
}
